import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, waLink } from "../lib/api";
import { Star, Clock, Check, MessageCircle, ChevronLeft, MapPin, Users, Calendar } from "lucide-react";
import LeadForm from "../components/LeadForm";
import Seo from "../components/Seo";

export default function PackageDetail() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  useEffect(() => { api.get(`/packages/${slug}`).then(r => setP(r.data)).catch(() => setP(false)); }, [slug]);

  if (p === null) return <div className="pt-32 container-p muted">Loading…</div>;
  if (p === false) return <div className="pt-32 container-p"><Link to="/packages" className="btn-outline">Back to packages</Link><p className="mt-6 muted">Package not found.</p></div>;

  const productSchema = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: p.title,
    image: [p.image],
    description: p.description,
    brand: { "@type": "Brand", name: "Acchajee Travels" },
    offers: { "@type": "Offer", price: p.price, priceCurrency: "INR", availability: "https://schema.org/InStock" },
    aggregateRating: { "@type": "AggregateRating", ratingValue: String(p.rating), reviewCount: String(p.reviews) },
  };

  return (
    <div className="pt-16 lg:pt-20">
      <Seo title={`${p.title} — ${p.duration} from ₹${p.price.toLocaleString("en-IN")}`} path={`/packages/${p.slug}`} description={p.description} image={p.image} type="product" schema={productSchema} />
      <div className="relative h-[60vh] overflow-hidden">
        <img src={p.image} alt={p.title} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-black/10" />
        <div className="container-p relative h-full flex flex-col justify-end pb-10 z-10 text-white">
          <Link to="/packages" className="inline-flex items-center gap-1 text-sm mb-4 opacity-90 hover:opacity-100"><ChevronLeft className="w-4 h-4" /> All Packages</Link>
          <span className="text-xs uppercase tracking-widest opacity-80">{p.destination} · {p.country}</span>
          <h1 className="h1 text-white max-w-3xl mt-2" data-testid="pkg-title">{p.title}</h1>
          <div className="flex flex-wrap gap-4 mt-4 text-sm">
            <span className="flex items-center gap-1.5"><Clock className="w-4 h-4" /> {p.duration}</span>
            <span className="flex items-center gap-1.5"><Star className="w-4 h-4 fill-[#FF7D00] text-[#FF7D00]" /> {p.rating} ({p.reviews} reviews)</span>
            <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4" /> {p.destination}</span>
          </div>
        </div>
      </div>

      <section className="container-p py-16 grid grid-cols-1 lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2">
          <h2 className="h2 mb-4">Overview</h2>
          <p className="muted leading-relaxed mb-8">{p.description}</p>

          <h3 className="h3 mb-4">Highlights</h3>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-10">
            {(p.highlights || []).map((h, i) => <li key={i} className="flex items-start gap-2 text-sm"><Check className="w-4 h-4 text-[#FF7D00] mt-0.5 flex-shrink-0" /> {h}</li>)}
          </ul>

          <h3 className="h3 mb-4">What's Included</h3>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {(p.inclusions || []).map((h, i) => <li key={i} className="flex items-start gap-2 text-sm"><Check className="w-4 h-4 text-[#006CE4] mt-0.5 flex-shrink-0" /> {h}</li>)}
          </ul>
        </div>

        <aside className="lg:col-span-1">
          <div className="sticky top-24 card-glass p-6">
            <div className="text-xs muted">Starting from</div>
            <div className="font-display font-bold text-4xl text-[#0A2540]">₹{p.price.toLocaleString("en-IN")}</div>
            <div className="text-sm muted">per person on twin sharing</div>
            <div className="mt-5 flex flex-col gap-2">
              <a href={waLink(`Hi, I'd like details on ${p.title} package (₹${p.price.toLocaleString("en-IN")}).`)} target="_blank" rel="noopener noreferrer" className="btn-wa justify-center py-3" data-testid="pkg-wa"><MessageCircle className="w-4 h-4" /> WhatsApp Enquiry</a>
              <a href="#lead-form" className="btn-primary justify-center" data-testid="pkg-quote">Get Free Quote</a>
            </div>
            <div className="mt-6 pt-6 border-t border-slate-100 space-y-3 text-sm">
              <div className="flex items-center gap-2 muted"><Calendar className="w-4 h-4" /> Flexible departure dates</div>
              <div className="flex items-center gap-2 muted"><Users className="w-4 h-4" /> Customizable for group size</div>
              <div className="flex items-center gap-2 muted"><Check className="w-4 h-4" /> No hidden charges</div>
            </div>
          </div>
        </aside>
      </section>

      <section className="container-p pb-20" id="lead-form">
        <div className="card-glass p-8 md:p-12">
          <LeadForm title={`Book ${p.title}`} defaultDestination={p.destination} source={`package-${p.slug}`} />
        </div>
      </section>
    </div>
  );
}
